import { create } from "zustand";
import debounce from "lodash.debounce";
import {
  fetchTrapSkus as apiFetchTrapSkus,
  fetchTrapShops as apiFetchTrapShops,
  updateTrapSkuPrice as apiUpdateTrapSkuPrice,
  updateTrapSkuCategory as apiUpdateTrapSkuCategory,
  updateTrapSkuPresence as apiUpdateTrapSkuPresence,
  fetchTrapProducts as apiFetchTrapProducts,
  toggleTrapAuto as apiToggleTrapAuto,
  massUpdateCustomPrices as apiMassUpdateCustomPrices,
  massResetCustomPrices as apiMassResetCustomPrices,
  updateTrapCustomPrice as apiUpdateTrapCustomPrice,
  removeTrapCustomPrice as apiRemoveTrapCustomPrice,
} from "../services/trapSkuService";
import {
  fetchTrapCategories as apiFetchTrapCategories,
  createTrapCategory as apiCreateTrapCategory,
  updateTrapCategory as apiUpdateTrapCategory,
} from "../services/trapCategoriesService";
import { fetchCrmCategories as apiFetchCrmCategories } from "../services/crmCategoriesService";
import { buildCategoryTree, flattenCategoryTree } from "utils";
import {
  buildShopFilterValue,
  parseShopFilterValue,
  getPlatformLabel,
} from "../utils/trapSkuConfig";

// Розкладає фільтр магазину "платформа:id" у параметри API
const buildApiParams = (filters) => {
  const { shop, platform, ...rest } = filters;
  const params = { ...rest };

  if (shop) {
    const { platform: shopPlatform, shopId } = parseShopFilterValue(shop);
    params.platform = shopPlatform;
    params.shop_id = shopId;
  } else if (platform) {
    params.platform = platform;
  }

  return params;
};

// Глобальний стор (state manager) сторінки "Товари ловушок"
export const useTrapSkuStore = create((set, get) => ({
  trapSkus: [],
  shops: [],
  trapCategories: [],
  trapCategoriesTree: [],
  crmCategories: [],
  totalCount: 0,
  isLoading: true,

  expandedTraps: {},

  filters: {
    search: "",
    shop: null, // "платформа:id"
    platform: null,
    category_id: null,
    only_custom: false,
    page: 1,
    page_size: 100,
    sort_by: "shop_name",
    sort_asc: true,
  },

  // Debounced-функція для відкладеного оновлення даних з сервера
  debouncedRefresh: debounce(() => {
    get().fetchTrapSkus();
  }, 20000),

  // Ініціалізує стор при першому завантаженні сторінки
  init: async () => {
    get().initFiltersFromURL();
    await get().fetchTrapSkus();
    await Promise.all([
      get().fetchShops(),
      get().fetchTrapCategories(),
      get().fetchCrmCategories(),
    ]);
  },

  // Зчитує параметри з URL і встановлює початковий стан фільтрів
  initFiltersFromURL: () => {
    const params = new URLSearchParams(window.location.search);

    // Сумісність зі старим ?shop_id= (магазин Prom)
    const legacyShopId = params.get("shop_id");
    const shop =
      params.get("shop") ||
      (legacyShopId ? buildShopFilterValue("prom", legacyShopId) : null);

    set({
      filters: {
        ...get().filters,
        search: params.get("search") || "",
        shop,
        platform: params.get("platform") || null,
        category_id: params.get("category_id") || null,
        only_custom: params.get("only_custom") === "true",
        page: parseInt(params.get("page") || "1", 10),
        sort_by: params.get("sort_by") || "shop_name",
        sort_asc: params.get("sort_asc") !== "false",
      },
    });
  },

  // Оновлює URL-рядок браузера відповідно до поточного стану фільтрів
  syncFiltersToURL: () => {
    const { filters } = get();
    const params = new URLSearchParams();

    // Додаємо в URL лише непусті значення для чистоти
    if (filters.search) params.set("search", filters.search);
    if (filters.shop) params.set("shop", filters.shop);
    if (filters.platform) params.set("platform", filters.platform);
    if (filters.category_id) params.set("category_id", filters.category_id);
    if (filters.only_custom) params.set("only_custom", "true");
    if (filters.page !== 1) params.set("page", filters.page);
    if (filters.sort_by !== "shop_name") params.set("sort_by", filters.sort_by);
    if (!filters.sort_asc) params.set("sort_asc", "false");

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  },

  // Керує логікою фільтрації таблиці
  applyFilters: (newFilters) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...newFilters,
        page: 1,
      },
    }));
    get().syncFiltersToURL();
    get().fetchTrapSkus();
  },

  // Керує логікою сортування таблиці
  setSort: (field) => {
    const { sort_by, sort_asc } = get().filters;

    if (sort_by === field) {
      // Зміна напрямку
      set((state) => ({
        filters: { ...state.filters, sort_asc: !sort_asc },
      }));
    } else {
      set((state) => ({
        filters: { ...state.filters, sort_by: field, sort_asc: true, page: 1 },
      }));
    }
    get().syncFiltersToURL();
    get().fetchTrapSkus();
  },

  // Керує логікою пагінації
  setPage: (page) => {
    set((state) => ({
      filters: { ...state.filters, page },
    }));
    get().syncFiltersToURL();
    get().fetchTrapSkus();
  },

  // Керує логікою пошуку
  setSearch: (value) => {
    set((state) => ({
      filters: {
        ...state.filters,
        search: value,
        page: 1,
      },
    }));
    get().syncFiltersToURL();
    get().fetchTrapSkus();
  },

  // Асинхронно завантажує список товарів з сервера
  fetchTrapSkus: async () => {
    get().debouncedRefresh.cancel();

    set({ isLoading: true });
    try {
      const { data, totalCount } = await apiFetchTrapSkus(buildApiParams(get().filters));
      set({ trapSkus: data, totalCount });
    } catch (err) {
      // У випадку помилки скидаємо дані до порожніх значень
      console.error("Failed to fetch trap SKUs:", err);
      set({ trapSkus: [], totalCount: 0 });
    } finally {
      set({ isLoading: false });
    }
  },

  // Функція для завантаження магазинів (Prom + Horoshop)
  fetchShops: async () => {
    try {
      const data = await apiFetchTrapShops();
      set({
        shops: data.map((s) => ({
          id: buildShopFilterValue(s.platform, s.shop_id),
          name: `${getPlatformLabel(s.platform)} · ${s.name}`,
        })),
      });
    } catch (err) {
      // У випадку помилки скидаємо дані до порожніх значень
      console.error("Failed to load shops:", err);
      set({ shops: [] });
    }
  },

  // Функція для завантаження категорій
  fetchTrapCategories: async () => {
    try {
      const data = await apiFetchTrapCategories();
      set({
        trapCategories: data.map((c) => ({
          value: c.id,
          label: c.name,
          crm_category_id: c.crm_category_id,
        })),
      });

      // Створюємо дерево категорій
      const tree = buildCategoryTree(data);
      const options = flattenCategoryTree(tree);
      set({ trapCategoriesTree: options });
    } catch (err) {
      // У випадку помилки скидаємо дані до порожніх значень
      console.error("Failed to load trap categories:", err);
      set({ trapCategories: [] });
      set({ trapCategoriesTree: [] });
    }
  },

  // Функція для завантаження категорій CRM
  fetchCrmCategories: async () => {
    try {
      const data = await apiFetchCrmCategories();
      set({ crmCategories: data.map((c) => ({ value: c.id, label: c.name })) });
    } catch (err) {
      console.error("Failed to load CRM categories:", err);
      set({ crmCategories: [] });
    }
  },

  // Оптимістично оновлює ціни товару ловушки
  updateTrapSkuPrice: async (sku, newPrice) => {
    // Миттєво оновлюємо UI, не чекаючи відповіді сервера
    set((state) => ({
      trapSkus: state.trapSkus.map((item) =>
        item.sku === sku ? { ...item, price: newPrice } : item,
      ),
    }));

    // У фоновому режимі надсилаємо запит на сервер
    apiUpdateTrapSkuPrice(sku, newPrice)
      .then(() => {
        // Успіх: запускаємо відкладене повне оновлення для синхронізації
        get().debouncedRefresh();
      })
      .catch((error) => {
        // Помилка: відкочуємо зміни, перезавантаживши дані з сервера
        console.error("Failed to update price, rolling back:", error);
        get().fetchTrapSkus();
      });
  },

  // Оптимістично встановлює кастомну ціну товару ловушки
  updateTrapCustomPrice: async (sku, newPrice) => {
    // Миттєво оновлюємо UI: ставимо нову ціну та позначку кастомної
    set((state) => ({
      trapSkus: state.trapSkus.map((item) =>
        item.sku === sku
          ? { ...item, price: newPrice, is_custom_price: true }
          : item,
      ),
    }));

    apiUpdateTrapCustomPrice(sku, newPrice)
      .then(() => {
        get().debouncedRefresh();
      })
      .catch((error) => {
        console.error("Failed to update custom price, rolling back:", error);
        get().fetchTrapSkus();
      });
  },

  // Оптимістично видаляє кастомну ціну, повертаючи товар до базової ціни
  removeCustomPrice: async (sku) => {
    set((state) => ({
      trapSkus: state.trapSkus.map((item) =>
        item.sku === sku
          ? { ...item, price: item.original_price, is_custom_price: false }
          : item,
      ),
    }));

    apiRemoveTrapCustomPrice(sku)
      .then(() => {
        get().debouncedRefresh();
      })
      .catch((error) => {
        console.error("Failed to remove custom price, rolling back:", error);
        get().fetchTrapSkus();
      });
  },

  // Масово встановлює кастомні ціни та перезавантажує список
  massUpdateCustomPrices: async (skus, markupType, markupValue) => {
    set({ isLoading: true });
    try {
      await apiMassUpdateCustomPrices(skus, markupType, markupValue);
      await get().fetchTrapSkus();
    } catch (error) {
      console.error("Failed to mass update custom prices:", error);
      set({ isLoading: false });
      throw error;
    }
  },

  // Масово скидає кастомні ціни та перезавантажує список
  massResetCustomPrices: async (skus) => {
    set({ isLoading: true });
    try {
      await apiMassResetCustomPrices(skus);
      await get().fetchTrapSkus();
    } catch (error) {
      console.error("Failed to reset custom prices:", error);
      set({ isLoading: false });
      throw error;
    }
  },

  // Оптимістично оновлює категорії товару ловушки
  updateTrapSkuCategory: async (sku, newCategoryId) => {
    // Миттєво оновлюємо UI, не чекаючи відповіді сервера
    const { trapCategories } = get();
    const newCategory = trapCategories.find((c) => c.value === newCategoryId);
    set((state) => ({
      trapSkus: state.trapSkus.map((item) =>
        item.sku === sku
          ? {
              ...item,
              category_id: newCategoryId,
              trap_category_name: newCategory ? newCategory.label : null,
            }
          : item,
      ),
    }));

    // У фоновому режимі надсилаємо запит на сервер
    apiUpdateTrapSkuCategory(sku, newCategoryId)
      .then(() => {
        // Успіх: запускаємо відкладене повне оновлення для синхронізації
        get().debouncedRefresh();
      })
      .catch((error) => {
        // Помилка: відкочуємо зміни, перезавантаживши дані з сервера
        console.error("Failed to update category, rolling back:", error);
        get().fetchTrapSkus();
      });
  },

  // Оптимістично оновлює наявності
  updateTrapSkuPresence: async (sku, newPresence) => {
    // Миттєво оновлюємо UI, не чекаючи відповіді сервера
    set((state) => ({
      trapSkus: state.trapSkus.map((item) =>
        item.sku === sku ? { ...item, presence: newPresence } : item,
      ),
    }));

    // У фоновому режимі надсилаємо запит на сервер
    apiUpdateTrapSkuPresence(sku, newPresence)
      .then(() => {
        // Успіх: запускаємо відкладене повне оновлення для синхронізації
        get().debouncedRefresh();
      })
      .catch((error) => {
        // Помилка: відкочуємо зміни, перезавантаживши дані з сервера
        console.error("Failed to update presence, rolling back:", error);
        get().fetchTrapSkus();
      });
  },

  // Створює нову категорію-пастку
  createTrapCategory: async (categoryData) => {
    try {
      await apiCreateTrapCategory(categoryData);
      await get().fetchTrapCategories();
    } catch (error) {
      console.error("Failed to create category:", error);
      await get().fetchTrapCategories();
    }
  },

  // Оновлює існуючу категорію-пастку
  updateTrapCategory: async (id, categoryData) => {
    try {
      await apiUpdateTrapCategory(id, categoryData);
      await get().fetchTrapCategories();
    } catch (error) {
      console.error("Failed to update category:", error);
      await get().fetchTrapCategories();
    }
  },

  // Функція для розгортання/згортання ловушки.
  // expand: true — примусово розгорнути, false — згорнути, undefined — перемкнути
  toggleExpand: async (sku, expand) => {
    const { expandedTraps } = get();
    const isCurrentlyExpanded = !!expandedTraps[sku];
    const shouldExpand = expand === undefined ? !isCurrentlyExpanded : expand;

    if (!shouldExpand) {
      // Згортаємо та видаляємо зі стану
      const updated = { ...expandedTraps };
      delete updated[sku];
      set({ expandedTraps: updated });
      return;
    }

    // Вже розгорнуто — повторно не завантажуємо
    if (isCurrentlyExpanded) return;

    // Продукти-переводи прив'язані до категорії ловушки
    const categoryId = get().trapSkus.find((t) => t.sku === sku)?.trap_category_id;

    // Розгортаємо та завантажуємо товари
    set({
      expandedTraps: {
        ...get().expandedTraps,
        [sku]: { products: [], loading: true },
      },
    });
    try {
      const response = categoryId
        ? await apiFetchTrapProducts(categoryId)
        : { products: [] };
      const products = response.products || [];
      // Перевіряємо, чи ще розгорнуто цей самий SKU
      if (get().expandedTraps[sku]) {
        set({
          expandedTraps: {
            ...get().expandedTraps,
            [sku]: { products, loading: false },
          },
        });
      }
    } catch (err) {
      console.error("Failed to fetch trap products:", err);
      if (get().expandedTraps[sku]) {
        set({
          expandedTraps: {
            ...get().expandedTraps,
            [sku]: { products: [], loading: false },
          },
        });
      }
    }
  },

  // Перемикає авто-режим наявності
  toggleAuto: async (sku, auto) => {
    // Оптимістичне оновлення
    set((state) => ({
      trapSkus: state.trapSkus.map((item) =>
        item.sku === sku
          ? {
              ...item,
              auto,
              // Якщо вимикаємо auto → ставимо not_available (як робить бекенд)
              ...(auto ? {} : { presence: "not_available" }),
            }
          : item,
      ),
    }));

    apiToggleTrapAuto(sku, auto)
      .then(() => {
        get().debouncedRefresh();
      })
      .catch((error) => {
        console.error("Failed to toggle auto:", error);
        get().fetchTrapSkus();
      });
  },
}));
